import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { ASC, IDLE, PENDING, YES } from "const"
import { Lifecycle, MtProject, MtDriveFile } from "server/models"
import { StateType, deepCopy } from "client/utils"
import { sort } from "client/store/utils"
import {
    StoreType,
    TaskLifecyclePhaseIds,
    TaskParticipantList,
    TaskObserverList,
    LifecyclePhaseList,
    AccountUserList,

    LoadTaskById,
    LoadTaskParticipants,
    LoadTaskObservers,
    LoadTaskAttachments,
    LoadAllTasks,
    LoadAllProjects,
    LoadAllLifecycles,
    LoadAllLifecyclePhases,
    LoadAccountUsers,
    LoadAllProjectUsers,
    LoadProjectUserGroups,
    LoadProjectPermissions,
    GetTaskAccess,
    PrepareTaskNestedIds,

    setTaskEdit,
    setTaskReloadEdit,
    setTaskParticipantList,
    setTaskObserverList,
    setTaskAttachmentsEdit,
    setTaskProjectGroupedListEdit,
    setTaskProjectGroupedIdsEdit,
    setTaskLifecyclePhaseListEdit,
    setTaskLifecyclePhaseIdsEdit,
    setProjectList,
    setAccountUserList,
    setProjectUserList,
    setSystemProjectUserGroups,
    setSystemProjectPermissions,

    initTask,
    moduleIds,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

interface EditTaskProps {
    taskId: number
    parentTaskId?: number
    description?: string
    attachments?: MtDriveFile[]
}

const Edit = ({
    taskId = null,
    parentTaskId = null,
    description = null,
    attachments = null,
}: EditTaskProps) => {
    const task = useSelector((state: StoreType) => state.task.taskEdit)
    const reloadEdit = useSelector((state: StoreType) => state.task.reloadEdit)

    const taskParticipantList = useSelector((state: StoreType) => state.taskParticipant.taskParticipantList)
    const taskObserverList = useSelector((state: StoreType) => state.taskObserver.taskObserverList)
    const project = useSelector((state: StoreType) => state.project.projectView)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const reload = () => {
        dispatch(setTaskReloadEdit(false))
        setTimeout(() => {
            dispatch(setTaskReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        const promiseArray: Promise<any>[] = [
            // load all tasks for all projets
            LoadAllTasks(userData.accountId, null, userData.userId).then((res) => {
                const groupedByProject = PrepareTaskNestedIds(Object.values(res), true)
                dispatch(setTaskProjectGroupedIdsEdit(groupedByProject))
                dispatch(setTaskProjectGroupedListEdit(res))
            }),
            LoadAllProjects(userData.accountId).then((res) => {
                dispatch(setProjectList(res))

                const phaseIds: TaskLifecyclePhaseIds = {}
                const phaseList: LifecyclePhaseList = {}
                const promiseNestedArray: Promise<any>[] = []

                Object.values(res).forEach((projectRow: MtProject) => {
                    promiseNestedArray.push(
                        // to check user access for project level
                        LoadProjectUserGroups(userData.userId, userData.accountId, projectRow.id)
                        .then((userGroups) => {
                            dispatch(setSystemProjectUserGroups(projectRow.id, userGroups))
                        }),
                        LoadProjectPermissions(userData.accountId, projectRow.id)
                        .then((permissions) => {
                            dispatch(setSystemProjectPermissions(projectRow.id, permissions.permissions))
                        }),
                    )

                    if (packageSettings.lifecycleModuleAccess !== YES) return

                    const projectModules = JSON.parse(projectRow.modules)
                    if (!projectModules[moduleIds.lifecycle]) return // check if lifecycle module is enabled in project

                    promiseNestedArray.push(
                        // load project lifecycles
                        LoadAllLifecycles(userData.accountId, projectRow.id)
                        .then(({ lifecycleList }) => {
                            const lifecycles: Lifecycle[] = Object.values(lifecycleList)
                            lifecycles.sort((a, b) => sort(a, b, "name", ASC))

                            const promiseLifecycleNestedArray: Promise<any>[] = []
                            lifecycles.forEach((lifecycle) => {
                                promiseLifecycleNestedArray.push(
                                    // load lifecycle phases
                                    LoadAllLifecyclePhases(lifecycle.id).then((phases) => {
                                        const phaseIdArray: number[] = []
                                        phases.sort((a, b) => sort(a, b, "name", ASC))
                                        phases.forEach((phase) => {
                                            phaseList[phase.id] = {
                                                ...phase,
                                                lifecycle,
                                            }
                                            phaseIdArray.push(phase.id)
                                        })

                                        if (!phaseIds[projectRow.id]) phaseIds[projectRow.id] = []
                                        phaseIds[projectRow.id] = phaseIds[projectRow.id].concat(phaseIdArray)
                                    }),
                                )
                            })
                            return Promise.all(promiseLifecycleNestedArray)
                        }),
                    )
                })
                return Promise.all(promiseNestedArray).then(() => {
                    dispatch(setTaskLifecyclePhaseListEdit(phaseList))
                    dispatch(setTaskLifecyclePhaseIdsEdit(phaseIds))
                })
            }),
            LoadTaskParticipants(taskId).then((res) => {
                const taskParticipantListCopy: TaskParticipantList = taskParticipantList ? deepCopy(taskParticipantList) : {}
                taskParticipantListCopy[taskId] = res
                dispatch(setTaskParticipantList(taskParticipantListCopy))
            }),
            LoadTaskObservers(taskId).then((res) => {
                const taskObserverListCopy: TaskObserverList = taskObserverList ? deepCopy(taskObserverList) : {}
                taskObserverListCopy[taskId] = res
                dispatch(setTaskObserverList(taskObserverListCopy))
            }),
        ]

        if (taskId !== 0) {
            promiseArray.push(
                LoadTaskAttachments(taskId).then((res) => {
                    const files: MtDriveFile[] = []
                    res.forEach((row) => {
                        files.push(row.file)
                    })
                    dispatch(setTaskAttachmentsEdit(files))
                }),
            )
        }

        if (!project.id) { // if we are on account level
            promiseArray.push(
                LoadAccountUsers(userData.accountId).then((res) => { // to assign users on account level
                    const newAccountUserList: AccountUserList = { [userData.accountId]: res }
                    dispatch(setAccountUserList(newAccountUserList))
                }),
                LoadAllProjectUsers().then((res) => { // to display user status for project level tasks
                    dispatch(setProjectUserList(res))
                }),
            )
        }

        if (taskId === 0) {
            dispatch(setTaskEdit({
                ...initTask,
                taskId: parentTaskId, // create subtask
                description: description || "", // create task based on comment
            }))
            dispatch(setTaskAttachmentsEdit(attachments || []))
        } else {
            promiseArray.push(
                LoadTaskById(taskId, userData.accountId).then((res) => {
                    dispatch(setTaskEdit(res))
                }),
            )
        }

        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
            reload()
        })
    }, [])


    const {
        addAccess,
        allowedToEdit,
    } = GetTaskAccess(task, taskParticipantList, taskObserverList, userData)


    if (taskId === 0 && !addAccess) return <AccessDenied />
    if (taskId !== 0 && !allowedToEdit) return <AccessDenied />
    if (!task) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    {task.id ? `${lang.editTask} # ${task.id}` : lang.addTask}
                </PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                {
                    !reloadEdit
                    && <Form task={task} />
                }
                {
                    reloadEdit
                    && <Form task={task} />
                }
            </PageContent>
        </Container>
    )
}
export default Edit
